<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import EventCheckpointAutocomplete from '../../../components/Filters/components/EventCheckpointAutocomplete.vue'
import DeviceSelector from '../../../components/Selector/components/DeviceSelector.vue'
import TableColumn from '../../../components/Table/TableColumn.vue'
import ClickableTypography from '../../../components/Typography/ClickableTypography.vue'
import TypographyItem from '../../../components/Typography/TypographyItem.vue'
import { DEVICE_ROUTES, EVENT_CHECKPOINT_ROUTES, EVENTS_ROUTES } from '../../../router/routes'
import { EDetectionActions } from '../../../store/detectionStore/DetectionStoreTypes'
import { EColors } from '../../../types/constants/ColorValues'
import { ELocalStorage } from '../../../types/enum/LocalStorageEnum'
import { EPresenceStatus } from '../../../types/enum/StatusEnum'
import type { LocalStorageFilter } from '../../../types/filters/DeviceFilters'
import type { CheckpointModel } from '../../../types/models/CheckpointModel'
import type { DetectionModel } from '../../../types/models/DetectionModel'
import type { EventModel } from '../../../types/models/EventModel'
import { EAdminPerm } from '../../../types/permissions/AdminPermissionsEnum'
import { formatTimestamp } from '../../../utils/dateUtils'
import { getItemFromStorage } from '../../../utils/localStorageUtils'
import { userHasPermission } from '../../../utils/permissionsUtils'
import { isMobile, isMobileEvent } from '../../../utils/viewsUtils'
import DetectionsDesktopFilters from '../DetectionsDesktopFilters.vue'
import DetectionsTableView from '../DetectionsTableView.vue'
import EPCFilter from '../Filters/EPCFilter.vue'

const { t } = useI18n()
const route = useRoute()

const showMobileFilters = ref(isMobile())
isMobileEvent(e => (showMobileFilters.value = e.matches))

const columns = computed(() => ({
    deviceName: { field: 'device_name', label: t('detection.table.deviceName') },
    epc: { field: 'epc', label: t('detection.table.epc') },
    poi_name: { field: 'poi_name', label: t('detection.table.poiName') },
    site_name: { field: 'site_name', label: t('detection.table.siteName') },
    timestamp: { field: 'timestamp', label: t('detection.model.timestamp') }
}))

const selectedEvent = ref(getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.event)
const selectedCheckpoint = ref(
    getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.checkpoint
)

const selectedArrivedAtFrom = ref<Date | undefined>()
const selectedArrivedAtTo = ref<Date | undefined>()
const selectedLeftAtFrom = ref<Date | undefined>()
const selectedLeftAtTo = ref<Date | undefined>()
const selectedStatus = ref<EPresenceStatus | undefined>(
    (route?.query?.status as EPresenceStatus) ?? EPresenceStatus.ALL
)
const selectedEpc = ref<string | undefined>()
const selectedDevice = ref<number | undefined>()

const filters = computed<any>(() => ({
    site_id: selectedEvent.value?.id,
    poi_id: selectedCheckpoint.value?.id,
    arrived_at_from: selectedArrivedAtFrom.value,
    arrived_at_to: selectedArrivedAtTo.value,
    left_at_from: selectedLeftAtFrom.value,
    left_at_to: selectedLeftAtTo.value,
    status: selectedStatus.value ? selectedStatus.value : undefined,
    epc: selectedEpc.value,
    device_id: selectedDevice.value?.toString()
}))

const isSuperAdmin = computed(() => userHasPermission(EAdminPerm.SUPER_ADMIN))

const handleRowClick = (row: DetectionModel) => {
    selectedEvent.value = { id: row.site_id, name: row.site_name }
    selectedCheckpoint.value = { id: row.poi_id, name: row.poi_name }
}

const exportFilters = ref<{ site?: EventModel; poi?: CheckpointModel }>({})
const exportFileNameData = computed(() => ({
    site_name: exportFilters.value?.site?.name,
    poi_name: exportFilters.value?.poi?.name
}))
const exportFiltersParsed = computed(() => ({
    site_id: exportFilters.value?.site?.id,
    poi_id: exportFilters.value?.poi?.id
}))
</script>

<template>
    <DetectionsTableView
        :storeAction="EDetectionActions.FETCH_DETECTIONS"
        :storeExportAction="EDetectionActions.EXPORT_DETECTIONS"
        :exportFileNameData="exportFileNameData"
        :disabledExport="!exportFileNameData.site_name"
        :hideExport="!isSuperAdmin"
        :filters="filters"
        :exportFilters="exportFiltersParsed"
        @click="handleRowClick">
        <template #exportFilters>
            <EventCheckpointAutocomplete
                :event="exportFilters.site"
                :checkpoint="exportFilters.poi"
                @selectEvent="exportFilters = { site: $event }"
                @selectCheckpoint="exportFilters = { ...exportFilters, poi: $event }" />
        </template>
        <template #filters>
            <DeviceSelector
                :selectedDevice="selectedDevice?.toString()"
                @select="selectedDevice = $event?.device_id ?? $event?.id" />
            <EPCFilter :epc="filters.epc" @change="selectedEpc = $event" />
            <DetectionsDesktopFilters
                :filters="filters"
                :selectedEvent="selectedEvent"
                :selectedCheckpoint="selectedCheckpoint"
                @selectEvent="selectedEvent = $event"
                @selectCheckpoint="selectedCheckpoint = $event"
                :gridSizeProps="2" />
        </template>
        <template #columns>
            <TableColumn field="device_name" :label="columns['deviceName'].label" v-slot="props">
                <ClickableTypography
                    v-if="props.row.device_available === 1"
                    :label="props.row.device_name"
                    :href="DEVICE_ROUTES.buildUrl(props.row.device_id)" />
            </TableColumn>
            <TableColumn field="epc" :label="columns['epc'].label" v-slot="props">
                <TypographyItem
                    v-if="!!props.row.withdrawal"
                    :label="props.row.epc"
                    :class="`has-text-${EColors.DANGER}`" />
            </TableColumn>
            <TableColumn v-slot="props" field="poi_name" :label="columns['poi_name'].label">
                <ClickableTypography
                    :label="props.row.poi_name"
                    :href="`${EVENT_CHECKPOINT_ROUTES.buildUrl(props.row.site_id)}/${
                        props.row.poi_id
                    }`" />
            </TableColumn>
            <TableColumn :label="columns['site_name'].label" v-slot="props">
                <ClickableTypography
                    :label="props.row.site_name"
                    :href="EVENTS_ROUTES.buildUrl(props.row.site_id)" />
            </TableColumn>
            <TableColumn field="timestamp" :label="columns['timestamp'].label" v-slot="props">
                {{ formatTimestamp(props.row.timestamp) }}
            </TableColumn>
        </template>
    </DetectionsTableView>
</template>
